import { useEffect, useState, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { preferLocalized } from '../../helpers/commonUtils';
import { generateGameDescription } from '../../helpers/gameDescription';
import { Button } from '../ui/button';
import { Panel } from '../ui/panel';
import type { GameCardProps } from './types';
import styles from './GameCard.module.css';

function VolatilityStars({ volatility }: { volatility: number }) {
    return (
        <span className={styles.volatilityStars}>
            {Array.from({ length: 5 }, (_, index) => (
                <span
                    key={index}
                    className={`${styles.volatilityStar} ${
                        index < volatility ? styles.starFilled : styles.starEmpty
                    }`}
                >
                    ★
                </span>
            ))}
        </span>
    );
}

function SpecItem({
    label,
    value,
    valueClassName,
}: {
    label: string;
    value: ReactNode;
    valueClassName?: string;
}) {
    if (value == null || value === '') {
        return null;
    }

    return (
        <div className={styles.param} role="listitem">
            <span className={styles.paramLabel} aria-hidden="true">
                {label}
            </span>
            <span className={`${styles.paramValue} ${valueClassName ?? ''}`}>{value}</span>
        </div>
    );
}

export function GameCard({ game, gameLanguage = null, onLaunch }: GameCardProps) {
    const navigate = useNavigate();
    const location = useLocation();
    const [logoFailed, setLogoFailed] = useState(false);

    useEffect(() => {
        setLogoFailed(false);
    }, [game.id]);

    const { parameters } = game;
    const mechanic = preferLocalized(gameLanguage?.mechanic, parameters.mechanic);
    const genres = preferLocalized(gameLanguage?.genres, parameters.genres);
    const hasParameters =
        Boolean(parameters.rtp?.trim()) ||
        parameters.volatility != null ||
        Boolean(parameters.grid?.trim()) ||
        Boolean(mechanic) ||
        Boolean(genres) ||
        Boolean(parameters.maxMultiplier?.trim());
    const showVersion =
        typeof window !== 'undefined' &&
        window.SHOW_GAMES_VERSION === true &&
        Boolean(game.version && game.version.trim() !== '');

    const description =
        preferLocalized(gameLanguage?.lobbyDescription, game.description) ||
        generateGameDescription(game);
    const comingSoonTitle =
        preferLocalized(gameLanguage?.comingSoonTitle, game.comingSoonTitle) || 'COMING SOON';
    const comingSoonBrief =
        preferLocalized(gameLanguage?.comingSoonBrief, game.comingSoonBrief) || '';

    const backgroundStyle = game.backgroundImagePath
        ? {
              backgroundImage: `url('${game.backgroundImagePath}')`,
              backgroundColor: '#1a1a1a',
          }
        : undefined;

    const showLogo = Boolean(game.logoImagePath) && !logoFailed;

    const canLaunch = !game.comingSoon;

    const handlePlay = () => {
        onLaunch?.(game);
    };

/*    const handleReadMore = () => {
        navigate(`/${AppRoutes.GAME}/${game.id}`, { state: { returnTo: location.pathname } });
    };*/

    return (
        <Panel
            className={`${styles.card} ${game.comingSoon ? styles.cardComingSoon : styles.cardAnimated}`}
            onClick={canLaunch ? handlePlay : undefined}
        >
            <div className={styles.image} style={backgroundStyle}>
                {game.comingSoon ? (
                    <span className={styles.comingSoonBadge}>COMING SOON</span>
                ) : null}
                {showLogo ? (
                    <img
                        src={game.logoImagePath ?? undefined}
                        alt={`${game.name} Logo`}
                        className={styles.logoOverlay}
                        onError={() => setLogoFailed(true)}
                    />
                ) : (
                    <div className={styles.logoPlaceholder}>{game.name.charAt(0)}</div>
                )}
            </div>

            <div className={`${styles.content} ${showVersion ? styles.contentWithVersion : ''}`}>
                <h2 className={styles.title}>{game.name}</h2>
                <p className={styles.description}>{description}</p>

                {hasParameters ? (
                    <section className={styles.specs} aria-label="Game specifications">
                        <div className={styles.specsInner} role="list">
                            <SpecItem
                                label="RTP"
                                value={parameters.rtp}
                                valueClassName={styles.paramRtp}
                            />
                            <SpecItem
                                label="Volatility"
                                value={
                                    parameters.volatility != null ? (
                                        <VolatilityStars volatility={parameters.volatility} />
                                    ) : null
                                }
                            />
                            <SpecItem label="Grid" value={parameters.grid} />
                            <SpecItem label="Mechanic" value={mechanic} />
                            <SpecItem label="Genres" value={genres} />
                            <SpecItem
                                label="Max multiplier"
                                value={parameters.maxMultiplier}
                                valueClassName={styles.paramHighlight}
                            />
                        </div>
                    </section>
                ) : null}

                {showVersion ? (
                    <p
                        className={`${styles.description} ${styles.version}`}
                        aria-label="Game version"
                    >
                        <span className={styles.versionLabel}>Version</span>
                        <span className={styles.versionValue}>{game.version}</span>
                    </p>
                ) : null}
            </div>

            <div className={styles.footer}>
                {game.comingSoon ? (
                    <div className={styles.comingSoonFooter}>
                        <div className={styles.comingSoonFooterTitle}>{comingSoonTitle}</div>
                        <div className={styles.comingSoonFooterBrief}>{comingSoonBrief}</div>
                    </div>
                ) : (
                    <div className={styles.actions}>
{/*                        <Button variant="primary" color="purple" size="md" onClick={handleReadMore}>
                            Read More
                        </Button>*/}
                        <Button
                            variant="primary"
                            color="cyan"
                            size="md"
                            className={styles.playButton}
                        >
                            Play Now →
                        </Button>
                    </div>
                )}
            </div>
        </Panel>
    );
}
